"use client";

import * as React from "react";

import { cn } from "@/lib/utils";
import { EyeIcon } from "lucide-react";
import { EyeOutlineIcon } from "@/utils/icons";

import { inputActionClassName, inputControlClassName, inputSurfaceClassName } from "./input-styles";

export type InputProps = React.ComponentPropsWithoutRef<"input"> & {
  /** Icons and units are outside the editable value. */
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  endAction?: React.ReactNode;
  containerClassName?: string;
  /** The field is as wide as its value. On by default with a unit after the value, which must follow it. */
  fitValue?: boolean;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, name, placeholder: userPlaceholder, autoComplete: userAutoComplete,
      startAdornment, endAdornment, endAction, containerClassName, fitValue: fitValueProp, ...props },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState<boolean>(false);
    const isPassword = type === "password";
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const setInputRef = React.useCallback((node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    }, [ref]);
    const [uncontrolledValue, setUncontrolledValue] = React.useState(props.defaultValue ?? "");
    const fitValue = fitValueProp ?? endAdornment != null;
    const displayedValue = props.value ?? uncontrolledValue;
    // Use provided placeholder from upstream (e.g., "Enter Password")
    const resolvedPlaceholder = userPlaceholder;
    // Compute sensible autocomplete defaults when not provided
    const lname = (name ?? "").toString().toLowerCase();
    let computedAutoComplete = userAutoComplete;
    if (!computedAutoComplete) {
      if (type === "email" || lname === "email") computedAutoComplete = "email";
      else if (isPassword || lname.includes("password")) {
        computedAutoComplete = lname.includes("new") || lname.includes("confirm") ? "new-password" : "current-password";
      } else if (lname === "username" || lname === "userName") computedAutoComplete = "username";
      else if (lname === "firstname" || lname === "firstName" || lname === "given-name" || lname === "given_name") computedAutoComplete = "given-name";
      else if (lname === "lastname" || lname === "lastName" || lname === "family-name" || lname === "family_name") computedAutoComplete = "family-name";
      else if (lname === "phone" || lname === "phonenumber" || lname === "phone_number") computedAutoComplete = "tel";
      else if (lname === "country") computedAutoComplete = "country-name";
    }

    const decorated = isPassword || startAdornment != null || endAdornment != null || endAction != null;
    const input = (
      <input
        name={name}
        type={showPassword ? "text" : type}
        className={cn(
          "h-12 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
          inputControlClassName,
          decorated ? "border-0 rounded-none shadow-none" : inputSurfaceClassName,
          className,
          // The right padding moves inside the value (see the mirror): the caret needs room there.
          fitValue && "col-start-1 row-start-1 w-0 min-w-full pr-0"
        )}
        data-disabled={!decorated && props.disabled ? true : undefined}
        ref={setInputRef}
        data-alpha-input=""
        placeholder={resolvedPlaceholder}
        autoComplete={computedAutoComplete}
        {...props}
        onChange={(event) => {
          if (fitValue && props.value === undefined) setUncontrolledValue(event.target.value);
          props.onChange?.(event);
        }}
      />
    );

    if (!decorated) return input;

    return (
      <div
        className={cn("relative flex min-w-0 w-full items-center", fitValue ? "gap-1" : "gap-2", inputSurfaceClassName, containerClassName)}
        aria-invalid={props["aria-invalid"]}
        data-disabled={props.disabled || undefined}
        onMouseDown={(event) => {
          // The empty part of a fitted field remains clickable. Native clicks on the text
          // keep their own caret position; buttons keep their normal focus behaviour.
          if (event.target !== event.currentTarget || props.disabled) return;
          event.preventDefault();
          const control = inputRef.current;
          control?.focus();
          if (control && ["text", "search", "tel", "url", "password"].includes(control.type)) {
            control.setSelectionRange(control.value.length, control.value.length);
          }
        }}
      >
        {startAdornment != null ? <span className="pointer-events-none ml-1 flex shrink-0 select-none items-center text-muted-foreground" aria-hidden>{startAdornment}</span> : null}
        {fitValue ? (
          <span className="grid min-w-0 shrink grid-cols-[minmax(0,max-content)]">
            {/* A mirror measures the actual font, separators and masked characters without
                relying on field-sizing support or moving the caret during edits. Its trailing
                spacer stands in for the right padding, so the field keeps the same width but
                the caret has room after the last digit: without it, Safari and Chrome on macOS
                (2px caret) scroll the value a pixel or two to the left when the field is focused. */}
            <span
              aria-hidden
              className={cn(inputControlClassName, className, "pointer-events-none invisible col-start-1 row-start-1 w-auto min-w-0 whitespace-pre pr-0")}
              style={props.style}
            >
              {String(displayedValue === "" ? (userPlaceholder || "0") : displayedValue)}
              <span className="inline-block w-1" />
            </span>
            {input}
          </span>
        ) : input}
        {endAdornment != null ? <span className="pointer-events-none mr-1 flex shrink-0 select-none items-center text-muted-foreground" aria-hidden>{endAdornment}</span> : null}
        {endAction != null ? <div className="ml-auto flex shrink-0 items-center">{endAction}</div> : null}
        {isPassword ? (
          <button
            type="button"
            disabled={props.disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setShowPassword(!showPassword)}
            className={inputActionClassName}
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-pressed={showPassword}
          >
            {showPassword ? <EyeOutlineIcon /> : <EyeIcon className="h-5 w-5" />}
          </button>
        ) : null}
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input };
